import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useCurrentUser } from '@/stores/useCurrentUser';
import getAddresses from '@/utils/apiRestCalls/getAddresses';
import type { AddressAutocompleteOption } from '@/utils/types/addressAutocompleteOption.interface';
import { useDebounce } from '@/utils/useDebounce';
import type { Address } from '@/utils/types/address.interface';

export const mapAddressToAddressAutocompleteOption = (
  address: Address,
): AddressAutocompleteOption => ({
  id: address.id,
  monolith_building_id: address.monolith_building_id,
  address_id: address.id,
  property_id: address.property?.id ?? '',
  name: address.address_line_1,
  property_name: address.property?.name ?? '',
  street: address.address_line_2 ?? '',
  city: address.city,
  state: address.state,
  zip_code: address.zip_code,
  enable_manual_dsdr_invitation: false,
  source: address.property?.source ?? '',
});

const mapAddressesToAddressAutocompleteOptions = (
  addresses: Address[],
): AddressAutocompleteOption[] => addresses.map(mapAddressToAddressAutocompleteOption);

const DEBOUNCE_DELAY = 500;

export const useAddresses = () => {
  const { user } = useCurrentUser();
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, DEBOUNCE_DELAY);

  const addressesQuery = useQuery({
    queryKey: ['addresses', user?.id, debouncedSearch],
    queryFn: () => getAddresses(debouncedSearch ? { search: debouncedSearch } : {}),
    select: mapAddressesToAddressAutocompleteOptions,
    placeholderData: keepPreviousData,
  });

  const isDebouncing = searchText !== debouncedSearch && searchText.length > 0;
  const isSearchLoading = isDebouncing || (addressesQuery.isFetching && searchText.length > 0);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value.trim());
  }, []);

  return {
    addresses: addressesQuery.data,
    isLoading: addressesQuery.isLoading && searchText.length === 0,
    isSearchLoading,
    onSearchChange: handleSearchChange,
  };
};
